import { type WalletClient, type TransactionRequest, encodeFunctionData, parseUnits } from "viem";
import type { TokenId } from "@/types";
import { gatewayV1Abi } from "@/abi";
import { TOKEN_CONFIG, getGatewayContractAddress } from "@/config";
import { getTokenAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { getKyberRouterData } from "../router/kyberRouter.service";
import type { TransactionHash } from "@/types";

/**
 * Prepares a zap in and lock transaction
 */
export const prepareZapInAndLockTransaction = async (
    tokenId: TokenId,
    amount: number,
    unwindingEpochs: number,
    to: string,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account || !walletClient.chain?.id) {
        throw new Error("Wallet account or chain ID is not available");
    }

    const tokenAddress = getTokenAddress(walletClient.chain.id, tokenId);
    const amountBigInt = parseUnits(`${amount}`, TOKEN_CONFIG[tokenId].decimals);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    try {
        // Get Kyber router data for the token swap
        const routerData = await getKyberRouterData(tokenId, amount, walletClient);

        return {
            to: contractAddress,
            data: encodeFunctionData({
                abi: gatewayV1Abi,
                functionName: "zapInAndLock",
                args: [tokenAddress, amountBigInt, routerData.routerAddress, routerData.callData, unwindingEpochs, to],
            }),
        };
    } catch (error) {
        throw new Error(
            `Failed to prepare zapInAndLock transaction for ${tokenId}: ${
                error instanceof Error ? error.message : String(error)
            }`,
        );
    }
};

/**
 * Estimates gas for a zap in and lock transaction
 */
export const estimateZapInAndLockTransactionGas = async (
    tokenId: TokenId,
    amount: number,
    unwindingEpochs: number,
    to: string,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareZapInAndLockTransaction(tokenId, amount, unwindingEpochs, to, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};

/**
 * Executes a zap in and lock transaction
 */
export const gatewayZapInAndLockTransaction = async (
    tokenId: TokenId,
    amount: number,
    unwindingEpochs: number,
    to: string,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareZapInAndLockTransaction(tokenId, amount, unwindingEpochs, to, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};
